/*
 * useEditPanel3.tsx - Edit an Entity
 * Created by Dr. Maximillian Dornseif 2021-09
 * Copyright (c) 2021, 2023 HUDORA GmbH
 *
 * Created by Dr. Maximillian Dornseif 2023-10-06 in huWaWi3 33.1.0
 * Copyright (c) 2023 HUDORA GmbH
 */

import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { DocumentNode } from 'graphql'
import React, { ReactElement, useCallback, useState } from 'react'

import { IEntity } from '../../types'
import { useHdDialog } from '../../WaWi/components/HdDialog'
import { HdEditIcon } from '../components/HdIcons'
import { EntityMutate3 } from '../components/ke/EntityMutate3'
import { TusePanelReturn } from './hookTypes'

export interface IEditPanel3Options {
  entity: IEntity
  schemaName: string // Name des Daten-Schemas, dass angezeigt wird
  mutation?: DocumentNode
}

/** Builds a Edit-Panel
 *
 * Returns `[commandBarItem, thePanel, myOpenPanel]`
 *
 * `commandBarItem` is a 'Button description' for use in a FluentUI CommandBar.
 * `thePanel` is a <MuiPanel> containing a <EntityMutate3/>
 * `myOpenPanel` is a function to open the panel
 *
 */
export function useEditPanel3(options: IEditPanel3Options): TusePanelReturn {
  const mutation = options.mutation
  const { openDialog, renderDialog } = useHdDialog('Datensatz bearbeiten')
  const [entity, setEntity] = useState<IEntity>()

  const myOpenPanel = useCallback(
    (e?: IEntity): void => {
      if (e) {
        setEntity(e)
      }
      openDialog()
    },
    [openDialog]
  )

  const renderOpenButton = useCallback(
    (e?: IEntity): ReactElement => (
      <IconButton size="small" color="primary" title="Bearbeiten" onClick={() => myOpenPanel(e)}>
        <HdEditIcon />
      </IconButton>
    ),
    [openDialog]
  )

  if (!mutation) {
    return [null, null, null, null]
  }

  const commandBarItem: ICommandBarItemProps = {
    key: 'edit',
    text: 'Datensatz bearbeiten',
    title: 'vorhanden Datensatz bearbeiten',
    ariaLabel: 'Datensatz bearbeiten',
    iconProps: { iconName: 'Edit' },
    iconOnly: false,
    onClick: openDialog,
  }

  const thePanel = renderDialog({
    renderBody: () => (
      <Box>
        <EntityMutate3
          isOpen={true}
          entity={options.entity || entity}
          schemaName={options.schemaName}
          mutation={mutation}
        />
      </Box>
    ),
  })

  return [commandBarItem, thePanel, myOpenPanel, renderOpenButton]
}
